import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  breadboxAPI,
  cached,
  getDimensionTypeIdentifiersPersisted,
} from "@depmap/api";
import { ExternalLink, WordBreaker } from "@depmap/common-components";
import { isPortal, toPortalLink } from "@depmap/globals";
import { serializeSliceQuery } from "@depmap/selects";
import Papa from "papaparse";
import type { Dataset, DimensionType, SliceQuery } from "@depmap/types";
import {
  getProteinStripScale,
  proteinStripCell,
  proteinStripHeader,
  PROTEIN_STRIP_COLUMN_WIDTH,
} from "./ProteinStrip";

export interface ColumnDisplayOptions {
  header?: ({
    label,
    defaultElement,
  }: {
    label: string;
    defaultElement: React.ReactNode;
  }) => React.ReactNode;
  // `columnStats` is supplied by the table, not the row -- the same aggregate
  // the magnitude bars use. A renderer that draws its value at a scale shared
  // with the rest of the column reads it; everything else ignores it.
  cell?: (args: {
    getValue: () => unknown;
    columnStats?: { min: number; max: number; maxLength?: number };
  }) => React.ReactNode;
  numericPrecision?: number;
  width?: number;
}

interface Parameters {
  index_type_name: string;
  slices: SliceQuery[]; // Make sure to memoize this!
  viewOnlySlices?: Set<SliceQuery>; // Make sure to memoize this!
  // The table's universe of rows, when the caller already knows it. Scopes every
  // fetch instead of pulling whole columns and discarding most of them — the
  // per-residue annotation columns run to tens of megabytes each, and a table
  // over one gene wants a handful of rows out of them.
  rowIds?: Set<string>; // Make sure to memoize this!
  getColumnDisplayOptions?: (
    sliceQuery: SliceQuery
  ) => ColumnDisplayOptions | null;
  // Bumped by SliceTable's "Try again" button. It's a dependency of the fetch
  // effect and nothing else, which is what makes a retry refetch even when the
  // request is identical to the one that just failed — the common case, since a
  // caller whose slices are a stable array (an imported JSON list, say) gives
  // this hook nothing else that changes.
  retryToken?: number;
}

// Types for better code clarity
export type SliceResponse = {
  ids: string[];
  labels: string[];
  values: (string | number | null)[];
};
type ColumnRenames = Record<string, string>;

// Why a slice column has no data. "dataset_removed" means the dataset no
// longer appears in the listing (a remembered column outliving a Breadbox
// release that retired its dataset, most commonly) and the column can never
// load again. "fetch_failed" means the dataset exists but its request failed,
// which may well be transient. The distinction drives the UI: a removed
// dataset's column offers removal inline; a transient failure does not, so
// users aren't trained to delete columns that would come back on reload.
export type SliceLoadFailure = "dataset_removed" | "fetch_failed";

export interface RowFilters {
  hideUnselectedRows: boolean;
  hideIncompleteRows: boolean;
  hideRowsWithNoSearchResults: boolean;
}

interface AlignedData {
  columns: Array<{
    id: string;
    accessorFn: (row: Record<string, unknown>) => unknown;
    header: () => React.ReactNode;
    meta: {
      idLabel: string;
      units: string;
      value_type: string | null;
      datasetName: string;
      csvHeader: string;
      sliceQuery: SliceQuery;
      isEditable: boolean;
      isViewable: boolean;
      loadFailure?: SliceLoadFailure;
      numericPrecision?: number;
      suppressValueTooltip?: boolean;
      headerMenuItems?: (
        | {
            label: string;
            icon: string;
            onClick: () => void;
            disabled?: boolean;
          }
        | {
            widget: "divider";
          }
      )[];
    };
  }>;
  data: Record<string, string | number | undefined>[];
  loading: boolean;
  // Non-null only while this hook's own fetch is in flight, so a consumer can
  // tell it apart from loading for some other reason. `total` counts the
  // slices being fetched, including the label slice at index 0.
  progress: { loaded: number; total: number } | null;
  error: string | null;
  entityLabel: string;
  exportToCsv: (options?: {
    rowFilter?: (row: Record<string, string | number | undefined>) => boolean;
    // When provided, the exported rows are ordered to match this array.
    // Rows not in this array (but passing rowFilter) are appended at the end.
    sortedRowIds?: string[];
    // When provided, only these columns are included in the export.
    visibleColumnIds?: string[];
    selectedRowIds?: Set<string>;
    // The columns to export, in export order. Defaults to the slice columns
    // this hook built. Callers that add columns of their own pass the merged
    // list, so the download matches what is on screen; a column opts out by
    // having an empty `meta.csvHeader`.
    columns?: {
      id: string;
      meta: { csvHeader: string };
      accessorFn: (row: Record<string, string | number | undefined>) => unknown;
    }[];
  }) => string;
}

/**
 * Generates a unique column key for table data alignment.
 * This ensures that the same identifier from different datasets gets unique
 * column keys, and that reindex_through chains are distinguished from flat
 * queries to the same leaf column.
 */
export function createUniqueColumnKey(slice: SliceQuery): string {
  if (slice.identifier === "label" && !slice.reindex_through) {
    return "label";
  }

  return serializeSliceQuery(slice);
}

/**
 * Extracts column rename mappings from dataset preprocessing metadata.
 *
 * This is a workaround for Breadbox's current limitations - we parse the preprocessing
 * string to find column renames that were applied during data ingestion. This helps us
 * display the original, more readable column names to users.
 *
 * TODO: Extend Breadbox API to expose rename mappings directly rather than requiring
 * string parsing of preprocessing metadata.
 */
async function extractColumnRenames(
  metadataDatasetId: string
): Promise<ColumnRenames> {
  try {
    const datasets = await cached(breadboxAPI).getDatasets();
    const metadata = datasets.find(({ id }) => id === metadataDatasetId);
    const datasetMetadata = metadata?.dataset_metadata;

    // Handle different dataset_metadata structures
    let preprocess: string | undefined;
    if (
      datasetMetadata &&
      typeof datasetMetadata === "object" &&
      "preprocess" in datasetMetadata
    ) {
      preprocess = datasetMetadata.preprocess;
    }

    if (!preprocess) {
      return {};
    }

    const renames: Record<string, string> = {};

    const copyColRegex = /CopyColumns\(column_names=\{([^}]*)\}\)/g;
    const hasLabelTransform = preprocess.includes("AppendIdsToLabels");
    let outerMatch: RegExpExecArray | null;

    // eslint-disable-next-line no-cond-assign
    while ((outerMatch = copyColRegex.exec(preprocess)) !== null) {
      const entryRegex = /'([^']+)'\s*:\s*'([^']+)'/g;
      let entryMatch: RegExpExecArray | null;

      // eslint-disable-next-line no-cond-assign
      while ((entryMatch = entryRegex.exec(outerMatch[1])) !== null) {
        if (entryMatch[2] === "label" && hasLabelTransform) {
          continue;
        }

        // Invert: value becomes key, key becomes value
        renames[entryMatch[2]] = entryMatch[1];
      }
    }

    return renames;
  } catch (error) {
    window.console.warn("Failed to extract column renames:", error);
    return {};
  }
}

const isLinkable = (dimension_type?: string | null) =>
  isPortal &&
  dimension_type &&
  ["depmap_model", "gene", "compound_v2"].includes(dimension_type);

function toDetailPageLink(id: string, label: string, dimension_type: string) {
  let href = "";

  if (dimension_type === "depmap_model") {
    href = toPortalLink(`/cell_line/${id}`);
  }

  if (dimension_type === "gene") {
    href = toPortalLink(`/gene/${label}`);
  }

  if (dimension_type === "compound_v2") {
    href = toPortalLink(`/compound/${label}`);
  }

  return <ExternalLink href={href}>{id}</ExternalLink>;
}

/**
 * Builds the list of slices to fetch, always including the required metadata columns.
 *
 * For every index type, we need:
 * 1. The "label" column from the metadata dataset (for human-readable names)
 * 2. Any user-requested slices
 */
function buildSlicesToFetch(
  indexType: DimensionType,
  userSlices: SliceQuery[]
): SliceQuery[] {
  if (!indexType.metadata_dataset_id) {
    throw new Error(
      `Dimension type "${indexType.name}" does not have a \`metadata_dataset_id\`!`
    );
  }

  const labelSlice: SliceQuery = {
    dataset_id: indexType.metadata_dataset_id,
    identifier_type: "column",
    identifier: "label",
  };

  // We always add an `id` and `label` column so filter out any redundancies.
  // Reindexed slices can never collide with the table's own metadata columns.
  const novelSlices = userSlices.filter((s) => {
    if (s.reindex_through) {
      return true;
    }

    if (s.identifier_type !== "column") {
      return true;
    }

    return s.identifier !== "label" && s.identifier !== indexType.id_column;
  });

  return [labelSlice, ...novelSlices];
}

/**
 * Fetches the data for a single slice using the dimension data API.
 *
 * This handles all slice types uniformly — tabular columns, matrix
 * features/samples, and reindex_through chains — by delegating to
 * the backend's dimension data endpoint.
 *
 * `rowIds` scopes the request to a caller-supplied row set (see `rowIds` in
 * SliceTable's props). It only applies to plain tabular columns: the backend
 * rejects indices alongside `reindex_through`, since the IDs would be ambiguous
 * between the root and the leaf of the chain, and matrix slices are addressed by
 * dimension rather than by row. Those keep fetching whole, which is what they did
 * before this existed.
 */
function canSubsetSlice(slice: SliceQuery): boolean {
  return slice.identifier_type === "column" && !slice.reindex_through;
}

function createDataFetchPromise(
  slice: SliceQuery,
  rowIds?: string[]
): Promise<SliceResponse> {
  return cached(breadboxAPI, { persist: true }).getDimensionData(
    slice,
    rowIds && canSubsetSlice(slice) ? rowIds : undefined
  );
}

/**
 * Resolves a human-readable display label for a slice.
 *
 * For reindex_through chains, shows every hop in the chain (root to leaf)
 * joined by " › ", e.g. "TestArmScreenID › ArmScreenID › KRAS" — no hop is
 * collapsed. Column headers are where a long, fully-spelled-out chain is
 * most useful: unlike the compact chain picker trigger, a header has the
 * room to show it, and collapsing hops can make two structurally different
 * columns (e.g. "label", which is present on every dimension type's own
 * metadata table, reached via different chains) show up with the exact
 * same header text.
 *
 * For tabular columns, the identifier is already the column name.
 *
 * For matrix features/samples, fetches metadata to find the label
 * corresponding to the identifier.
 */
export async function resolveDisplayLabel(
  slice: SliceQuery,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  indexType: DimensionType
): Promise<string> {
  const leafLabel = await resolveLeafLabel(slice);

  if (!slice.reindex_through) {
    return leafLabel;
  }

  // Collect hops leaf-to-root, then reverse for root-to-leaf display order.
  const hops: string[] = [];
  let current: SliceQuery | undefined = slice.reindex_through;

  while (current) {
    hops.push(current.identifier);
    current = current.reindex_through;
  }

  return [...hops.reverse(), leafLabel].join(" › ");
}

/**
 * Resolves the label for a single (non-chain) slice. For tabular columns
 * the identifier is the column name; for matrix features/samples we look
 * up the label from the dataset's own metadata.
 *
 * Axis is derived from `identifier_type` rather than an outer `indexType`,
 * because for reindex_through chains the leaf's dataset has no relation
 * to the query dimension's axis.
 */
async function resolveLeafLabel(slice: SliceQuery): Promise<string> {
  if (slice.identifier_type === "column") {
    return slice.identifier;
  }

  const isFeatureAxis = slice.identifier_type.startsWith("feature");
  const metadata = isFeatureAxis
    ? await cached(breadboxAPI, { persist: true }).getDatasetFeatures(
        slice.dataset_id
      )
    : await cached(breadboxAPI, { persist: true }).getDatasetSamples(
        slice.dataset_id
      );

  const idOrLabel = slice.identifier_type.endsWith("_id") ? "id" : "label";
  const match = metadata.find((item) => item[idOrLabel] === slice.identifier);
  return match?.label || slice.identifier;
}

// What resolveDisplayLabel would show, minus any lookups: raw identifiers
// only. Used for slices that failed to load, where a metadata fetch would
// just be another doomed request.
function fallbackDisplayLabel(slice: SliceQuery): string {
  const hops: string[] = [];
  let current = slice.reindex_through;

  while (current) {
    hops.push(current.identifier);
    current = current.reindex_through;
  }

  return [...hops.reverse(), slice.identifier].join(" › ");
}

const truncateMiddle = (str: string, maxLength = 45): string => {
  if (str.length <= maxLength) {
    return str;
  }

  const ellipsis = "…";
  const charsToShow = maxLength - ellipsis.length;
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);

  const start = str.slice(0, frontChars);
  const end = str.slice(-backChars);

  return `${start}${ellipsis}${end}`;
};

/**
 * Transforms raw API responses into a unified table structure.
 *
 * This function:
 * 1. Collects all unique row IDs across all data slices
 * 2. Creates rows where each ID gets values from all slices (with null for missing data)
 * 3. Generates table column definitions with proper headers and metadata
 */
export function transformToTableData(
  dataResponses: SliceResponse[],
  displayLabels: string[],
  slices: SliceQuery[],
  viewOnlySlices: Set<SliceQuery> | undefined,
  datasets: Dataset[],
  indexType: DimensionType,
  idColumnDisplayName: string,
  labelColumnDisplayName: string,
  idToLabelMappings: Record<string, Record<string, string>>,
  getColumnDisplayOptions?: Parameters["getColumnDisplayOptions"],
  // Aligned with `slices`. A failed slice gets a stub column (header names
  // the problem, cells stay empty) rather than failing the whole table.
  failures?: (SliceLoadFailure | null)[],
  // True when the caller scoped the table with `rowIds`. Only narrows which
  // slices get the foreign-ID diagnostic below — dropping is unchanged.
  isScoped = false
) {
  // Step 1: Create unique column keys, validate IDs, and key data by ID.
  //
  // The label response (always at index 0 — `buildSlicesToFetch` prepends
  // it) is keyed by the index type's canonical entity IDs. Every other
  // response should be keyed by a subset of those: a data slice can have
  // coverage holes, but should never include IDs that aren't part of the
  // index type. If it does, drop the offending IDs (so the table stays
  // coherent) and log loudly so the bug isn't invisible. This usually
  // means a slice that should have been wrapped in `reindex_through`
  // wasn't, or the backend returned IDs in the wrong space.
  //
  // Except for the slices a scoped table could not narrow. Under `rowIds` only
  // plain tabular columns come back subsetted; a matrix slice or a
  // reindex_through chain still returns the whole dimension type, so every row
  // outside the scope is "foreign" by this test. Those are dropped exactly as
  // before — they are outside the table's universe — but they are expected, not
  // a bug, and the diagnostic would be pure noise. A slice that *was* subsetted
  // is still checked: if that one comes back out of scope, something is wrong.
  const columnKeys = slices.map(createUniqueColumnKey);
  const labelIds = new Set(dataResponses[0].ids);
  const columnData: Record<string, Record<string, string | number | null>> = {};

  // Process each data response, dropping foreign IDs.
  dataResponses.forEach((response, index) => {
    const uniqueKey = columnKeys[index];
    const keyed: Record<string, string | number | null> = {};

    if (index > 0 && (!isScoped || canSubsetSlice(slices[index]))) {
      const foreign = response.ids.filter((id) => !labelIds.has(id));
      if (foreign.length > 0) {
        const slice = slices[index];
        // eslint-disable-next-line no-console
        console.error(
          `[SliceTable] Slice "${displayLabels[index]}" (dataset_id=` +
            `"${slice.dataset_id}", identifier="${slice.identifier}") ` +
            `returned ${foreign.length} of ${response.ids.length} IDs that ` +
            `aren't part of the "${indexType.name}" index. These rows are ` +
            `being dropped from the table. This usually means the slice ` +
            `should have been wrapped in reindex_through but wasn't. ` +
            `Foreign ID sample: ${JSON.stringify(foreign.slice(0, 5))}`
        );
      }
    }

    for (let i = 0; i < response.ids.length; i++) {
      if (index > 0 && !labelIds.has(response.ids[i])) {
        continue;
      }
      keyed[response.ids[i]] = response.values[i];
    }

    columnData[uniqueKey] = keyed;
  });

  // Step 2: Build data rows from the canonical entity set.
  const data = Array.from(labelIds).map((rowId) => {
    const row: Record<string, string | number | undefined> = { id: rowId };

    columnKeys.forEach((columnKey) => {
      // Use `undefined` instead of `null` for missing values because it
      // works better with table sorting (react-table columns have a
      // `sortUndefined: "last"` option but no equivalent for nulls).
      row[columnKey] = columnData[columnKey]?.[rowId] ?? undefined;
    });

    return row;
  });

  const ID_AND_LABEL_COLUMN_SIZE = 160;

  const shouldRenderIdsAsLinks = isLinkable(indexType.name);

  // Check for display options on the id column so consumers can
  // customize its width via getColumnDisplayOptions.
  const idDisplayOptions =
    getColumnDisplayOptions?.({
      dataset_id: indexType.metadata_dataset_id!,
      identifier_type: "column",
      identifier: indexType.id_column,
    }) ?? null;

  // Step 3: Build column definitions
  const idColumn = {
    id: "id",
    accessorFn: (row: Record<string, unknown>) => row.id,
    meta: {
      isEditable: false,
      isViewable: false,
      idLabel: idColumnDisplayName,
      units: "",
      datasetName: "",
      value_type: "text",
      csvHeader: idColumnDisplayName,
      sliceQuery: {
        dataset_id: indexType.metadata_dataset_id!,
        identifier_type: "column" as const,
        identifier: indexType.id_column,
      },
    },
    header: () => idColumnDisplayName,
    size: idDisplayOptions?.width ?? ID_AND_LABEL_COLUMN_SIZE,
    cell: ({
      getValue,
      row,
    }: {
      getValue: () => unknown;
      row: { original: Record<string, unknown> };
    }) => {
      const id = getValue() as string;
      const label = row.original.label as string;

      return id != null && shouldRenderIdsAsLinks
        ? toDetailPageLink(id, label, indexType.name)
        : id;
    },
  };

  const dataColumns = slices.map((slice, index) => {
    const columnKey = columnKeys[index];

    let displayLabel = displayLabels[index];

    // Special case: use the readable name for the label column (always first slice)
    if (index === 0 && slice.identifier === "label") {
      displayLabel = labelColumnDisplayName;
    }

    const dataset = datasets.find(
      (d) => d.given_id === slice.dataset_id || d.id === slice.dataset_id
    );

    // A slice can outlive its dataset (a remembered column pointing at a
    // dataset retired by a newer Breadbox release). Render a stub column
    // instead of failing the whole table: the header names the problem, and
    // the removal affordances still work because the stub keeps its
    // sliceQuery.
    const loadFailure =
      failures?.[index] ?? (dataset ? null : ("dataset_removed" as const));

    if (loadFailure) {
      const failureLabel = displayLabel || slice.identifier;

      return {
        size: undefined as number | undefined,
        id: columnKey,
        meta: {
          isEditable: columnKey !== "label" && !viewOnlySlices?.has(slice),
          isViewable: false,
          idLabel: failureLabel,
          units: "",
          value_type: null,
          datasetName: dataset?.name || "",
          // An empty csvHeader opts the column out of CSV export.
          csvHeader: "",
          sliceQuery: slice,
          loadFailure,
        },
        accessorFn: (row: Record<string, unknown>) => row[columnKey],
        header: () => (
          <div>
            <WordBreaker text={truncateMiddle(failureLabel)} />
            <br />
            <i>
              {loadFailure === "dataset_removed"
                ? "dataset no longer available"
                : "failed to load"}
            </i>
          </div>
        ),
        cell: () => <></>,
      };
    }

    if (!dataset) {
      // Unreachable (a missing dataset is classified as a failure above);
      // this exists so TypeScript can narrow `dataset` below.
      throw new Error(`Unknown dataset "${slice.dataset_id}"`);
    }

    // Suppress the dataset name only when the column comes from the index
    // type's own metadata dataset — otherwise a tabular column from a
    // non-metadata table (e.g. "Anchor Screen Table") would render with an
    // ambiguous header that hides why its data are sparse.
    const isMetadataDataset =
      dataset.id === indexType.metadata_dataset_id ||
      dataset.given_id === indexType.metadata_dataset_id ||
      dataset.given_id?.endsWith("_metadata");

    const datasetName =
      slice.identifier_type === "column" && isMetadataDataset
        ? ""
        : dataset.name || "";

    const units =
      dataset.format === "matrix_dataset"
        ? dataset.units
        : dataset.columns_metadata[slice.identifier]?.units;

    const value_type =
      dataset.format === "matrix_dataset"
        ? dataset.value_type
        : dataset.columns_metadata[slice.identifier]?.col_type;

    const references =
      dataset.format === "tabular_dataset"
        ? dataset.columns_metadata[slice.identifier]?.references
        : null;

    // Get per-column display options from the consumer
    const displayOptions = getColumnDisplayOptions
      ? getColumnDisplayOptions(slice)
      : null;

    // Non-null for the handful of columns that hold one character per residue,
    // which are unreadable as text and render as a colored band instead. Looked
    // up here rather than left to each consumer so that every SliceTable gets
    // it -- the columns are addable from the "Add Column" menu of any table
    // over the right index type, and a band that only appeared in the one table
    // that remembered to wire it up would be the odd behaviour to explain.
    const stripScale = getProteinStripScale(slice);

    return {
      size:
        displayOptions?.width ??
        (stripScale ? PROTEIN_STRIP_COLUMN_WIDTH : undefined) ??
        (columnKey === "label" ? ID_AND_LABEL_COLUMN_SIZE : undefined),
      id: columnKey,
      meta: {
        isEditable: columnKey !== "label" && !viewOnlySlices?.has(slice),
        isViewable: columnKey !== "label",
        idLabel: displayLabel,
        units,
        value_type,
        datasetName,
        csvHeader: [displayLabel, units, datasetName]
          .filter(Boolean)
          .join(" | "),
        sliceQuery: slice,
        // The band replaces the value rather than abbreviating it, so the
        // table's truncation tooltip has nothing useful to say -- it would pop
        // up the several-hundred-character string the band exists to spare the
        // reader.
        ...(stripScale && { suppressValueTooltip: true }),
        ...(displayOptions?.numericPrecision != null && {
          numericPrecision: displayOptions.numericPrecision,
        }),
      },
      accessorFn: (row: Record<string, unknown>) => row[columnKey],
      header: () => {
        const defaultElement = (
          <div>
            <WordBreaker text={truncateMiddle(displayLabel)} />
            {datasetName && (
              <>
                <br />
                <WordBreaker text={datasetName} />
              </>
            )}
          </div>
        );

        if (displayOptions?.header) {
          return displayOptions.header({
            label: displayLabel,
            defaultElement,
          });
        }

        // The band needs its key in the header, since some of its colors are
        // too low-contrast to carry meaning on their own.
        if (stripScale) {
          return proteinStripHeader(stripScale)({ defaultElement });
        }

        return defaultElement;
      },
      // Custom cell renderer from getColumnDisplayOptions takes highest priority.
      // When provided, it fully overrides the cell (magnitude bars won't apply).
      ...(displayOptions?.cell && {
        cell: displayOptions.cell,
      }),
      // Built-in cell renderer for per-residue strings (only if no custom
      // cell). Ahead of the others because a column that qualifies is never
      // also a reference or a string list.
      ...(!displayOptions?.cell &&
        stripScale && {
          cell: proteinStripCell(stripScale),
        }),
      // Built-in cell renderer for linkable references (only if no custom cell).
      ...(!displayOptions?.cell &&
        isLinkable(references) && {
          cell: ({
            getValue,
          }: {
            getValue: () => unknown;
            row: { original: Record<string, unknown> };
          }) => {
            const value = getValue();

            return value == null ? (
              <></>
            ) : (
              toDetailPageLink(
                getValue() as string,
                idToLabelMappings[references!][value as string],
                references as string
              )
            );
          },
        }),
      // Built-in cell renderer for string lists (only if no custom cell).
      ...(!displayOptions?.cell &&
        value_type === "list_strings" && {
          cell: ({ getValue }: { getValue: () => unknown }) => {
            const value = getValue();
            return value == null ? <></> : (value as string[]).join(", ");
          },
        }),
    };
  });

  return {
    data,
    columns: [idColumn, ...dataColumns],
  };
}

type TableRow = Record<string, string | number | undefined>;

/**
 * Fold a freshly built row set into the previous one when the two describe the
 * same rows, returning the PREVIOUS array so its identity is preserved.
 *
 * TanStack memoizes its row model on the `data` array's identity, and building
 * that model costs roughly 4.7us per row — 1.4s for a 300K-row table, which is
 * 87% of what adding a column used to cost. Adding or removing a column does
 * not change which rows exist, only what each one holds, so returning a new
 * array makes TanStack discard 300K `Row` objects and rebuild identical ones.
 *
 * Every value is rewritten, not just the columns that appear to have changed.
 * Rewriting all of them costs 41ms against the 1.4s rebuild it avoids, and it
 * means a slice whose values really did change cannot leave a stale cell
 * behind. Keys belonging to removed columns are deleted, so consumers that
 * enumerate a row (`hideIncompleteRows` walks `Object.entries`) don't see a
 * column that is no longer displayed.
 *
 * Any difference in the row set at all — a different index type, an added or
 * removed row, a reordering — falls back to the new array, since then the row
 * model genuinely does have to be rebuilt.
 *
 * One caveat, and the reason this is safe here specifically: a reused `Row`
 * keeps TanStack's `_valuesCache`, so a cell that was already rendered would
 * keep its old value if the underlying response changed. Breadbox responses
 * are immutable at their dataset address — the same invariant the persistent
 * API cache is built on (see persistentApiCache.ts) — so within a session the
 * same slice cannot come back with different values.
 */
export function reconcileRows(
  prev: TableRow[] | null,
  next: TableRow[]
): TableRow[] {
  if (!prev || prev.length === 0 || prev.length !== next.length) {
    return next;
  }

  for (let i = 0; i < next.length; i += 1) {
    if (prev[i].id !== next[i].id) {
      return next;
    }
  }

  // Every row carries every column key (transformToTableData assigns
  // `undefined` rather than skipping), so one row is a faithful sample.
  const nextKeys = Object.keys(next[0]);
  const staleKeys = Object.keys(prev[0]).filter((k) => !nextKeys.includes(k));

  for (let i = 0; i < prev.length; i += 1) {
    const target = prev[i];
    const source = next[i];

    for (let k = 0; k < nextKeys.length; k += 1) {
      target[nextKeys[k]] = source[nextKeys[k]];
    }

    for (let k = 0; k < staleKeys.length; k += 1) {
      delete target[staleKeys[k]];
    }
  }

  return prev;
}

/**
 * Custom hook for aligning disparate data slices to a shared index.
 *
 * This hook takes an index type and a list of data slices, then:
 * 1. Fetches metadata about the index type (including display name mappings)
 * 2. Fetches data for each slice using the appropriate API (tabular vs matrix)
 * 3. Aligns all data to the shared index ID space
 * 4. Returns table-ready data structures for @tanstack/react-table
 *
 * Row filtering is NOT applied here. The full unfiltered dataset is returned
 * so that ReactTable can compute stable column statistics (for magnitude bars)
 * from the complete data. Filtering is handled by ReactTable's `rowFilter` prop.
 */
export default function useAlignedData({
  index_type_name,
  slices,
  viewOnlySlices = undefined,
  rowIds = undefined,
  getColumnDisplayOptions = undefined,
  retryToken = 0,
}: Parameters): AlignedData {
  const [state, setState] = useState<AlignedData>({
    columns: [],
    data: [],
    // True from the start: the mount effect always begins a load, but effects
    // run after the first paint, so initializing to false painted one frame
    // of "There are no rows to display" before the spinner. That frame was
    // invisible when every load took a network round trip; with persistent-
    // cache hits it can be most of what the user sees.
    loading: true,
    progress: null,
    error: null,
    entityLabel: "",
    exportToCsv: () => "",
  });

  // Use refs to store metadata that triggers the data fetch effect
  const indexTypeRef = useRef<DimensionType | null>(null);
  const idColumnDisplayNameRef = useRef<string>("");

  // The row array handed to the table last time, so a column change can reuse
  // it instead of forcing a full row-model rebuild. See `reconcileRows`.
  const prevRowsRef = useRef<TableRow[] | null>(null);

  // Create CSV export callback that has access to current state.
  // Accepts an optional rowFilter to export only visible rows, or
  // exports all rows when no filter is provided.
  const exportToCsv = useCallback(
    (
      options: {
        rowFilter?: (
          row: Record<string, string | number | undefined>
        ) => boolean;
        sortedRowIds?: string[];
        visibleColumnIds?: string[];
        selectedRowIds?: Set<string>;
        columns?: {
          id: string;
          meta: { csvHeader: string; numericPrecision?: number };
          accessorFn: (
            row: Record<string, string | number | undefined>
          ) => unknown;
        }[];
      } = {}
    ) => {
      const {
        rowFilter,
        sortedRowIds,
        visibleColumnIds,
        selectedRowIds,
        columns: overrideColumns,
      } = options;

      if (!state.columns.length || !state.data.length) {
        return "";
      }

      const sourceColumns = overrideColumns ?? state.columns;

      // Filter columns to only visible ones if specified
      const visibleColumnIdSet = visibleColumnIds
        ? new Set(visibleColumnIds)
        : null;
      const columnsToExport = (visibleColumnIdSet
        ? sourceColumns.filter((col) => visibleColumnIdSet.has(col.id))
        : sourceColumns
      ).filter((col) => col.meta.csvHeader);

      // Apply the row filter if provided
      const filteredData = rowFilter
        ? state.data.filter(rowFilter)
        : state.data;

      // Sort to match display order if sortedRowIds is provided
      let dataToExport = filteredData;
      if (sortedRowIds && sortedRowIds.length > 0) {
        const orderMap = new Map(sortedRowIds.map((id, index) => [id, index]));

        dataToExport = [...filteredData].sort((a, b) => {
          const aOrder = orderMap.get(a.id as string) ?? Infinity;
          const bOrder = orderMap.get(b.id as string) ?? Infinity;
          return aOrder - bOrder;
        });
      }

      // Determine if we should include the selection column
      const shouldIncludeSelectionColumn =
        selectedRowIds && selectedRowIds.size > 0;

      // Create headers from column definitions
      const headers = columnsToExport.map((column) => column.meta.csvHeader);

      // Insert selection column header after ID (second position) if needed
      if (shouldIncludeSelectionColumn) {
        headers.splice(1, 0, "Selected");
      }

      // Transform data to match column order and convert values to strings
      const csvData = dataToExport.map((row) => {
        const rowData = columnsToExport.map((column) => {
          const value = column.accessorFn(row);
          // Handle null, undefined, and other falsy values
          if (value === null || value === undefined) {
            return "";
          }
          // Match what's shown on screen (see TableCell's formatNumber).
          if (
            typeof value === "number" &&
            column.meta.numericPrecision != null
          ) {
            return value.toFixed(column.meta.numericPrecision);
          }
          return String(value);
        });

        // Insert selection status after ID (second position) if needed
        if (shouldIncludeSelectionColumn) {
          const rowId = row.id as string;
          const isSelected = selectedRowIds.has(rowId);
          rowData.splice(1, 0, isSelected ? "Yes" : "No");
        }

        return rowData;
      });

      // Use Papaparse to generate CSV string
      return Papa.unparse({
        fields: headers,
        data: csvData,
      });
    },
    [state.columns, state.data]
  );

  // Combined effect: Load index type metadata and data
  useEffect(() => {
    let isCancelled = false;

    const loadData = async () => {
      setState((prev) => ({
        ...prev,
        loading: true,
        progress: null,
        error: null,
      }));

      try {
        // Step 1: Load index type metadata
        const indexType = await cached(breadboxAPI).getDimensionType(
          index_type_name
        );

        if (isCancelled) return;

        let idColumnDisplayName = indexType.id_column;

        // Try to get the original, more readable column names
        let labelColumnDisplayName = "label";

        if (indexType.metadata_dataset_id) {
          const renames = await extractColumnRenames(
            indexType.metadata_dataset_id
          );
          idColumnDisplayName =
            renames[indexType.id_column] || indexType.id_column;
          labelColumnDisplayName = renames.label || "label";
        }

        if (isCancelled) return;

        // Store in refs for dependency tracking
        indexTypeRef.current = indexType;
        idColumnDisplayNameRef.current = idColumnDisplayName;

        // Step 2: Figure out which slices still point at a dataset that
        // exists. A slice can outlive its dataset (a remembered column
        // pointing at a dataset retired by a newer Breadbox release); those
        // become stub columns instead of doomed fetches.
        const datasets = await cached(breadboxAPI).getDatasets();

        if (isCancelled) return;

        const slicesToFetch = buildSlicesToFetch(indexType, slices);

        const datasetExists = (dataset_id: string) =>
          datasets.some(
            (d) => d.id === dataset_id || d.given_id === dataset_id
          );

        // Index 0 is the label slice from the index type's own metadata;
        // without it there are no rows at all, so it keeps the hard-failure
        // behavior below.
        const failures: (SliceLoadFailure | null)[] = slicesToFetch.map(
          (slice, i) =>
            i > 0 && !datasetExists(slice.dataset_id) ? "dataset_removed" : null
        );

        const EMPTY_RESPONSE: SliceResponse = {
          ids: [],
          labels: [],
          values: [],
        };

        setState((prev) => ({
          ...prev,
          progress: { loaded: 0, total: slicesToFetch.length },
        }));

        // Materialized once rather than per slice: `indices` lands in the request
        // body, which is the persistent cache key, so a stable order across slices
        // (and across reloads) is what makes those keys hit.
        const indices = rowIds ? Array.from(rowIds).sort() : undefined;

        let loaded = 0;
        const onSliceSettled = () => {
          loaded += 1;

          if (isCancelled) {
            return;
          }

          setState((prev) =>
            prev.progress
              ? { ...prev, progress: { ...prev.progress, loaded } }
              : prev
          );
        };

        // Step 3: Load each slice's data and its display label together. A
        // single slice's failure degrades to a stub column rather than
        // rejecting the whole table.
        //
        // The two run concurrently rather than as sequential passes, since
        // label resolution costs a request for matrix feature/sample slices
        // and used to start only after every column's data had landed.
        const settled = await Promise.all(
          slicesToFetch.map(
            (slice, i): Promise<[SliceResponse, string]> => {
              if (failures[i]) {
                onSliceSettled();
                return Promise.resolve([
                  EMPTY_RESPONSE,
                  fallbackDisplayLabel(slice),
                ]);
              }

              // Index 0 is the label slice: without it there are no rows, so it
              // keeps no `.catch` and fails the whole table via the outer try.
              const dataPromise =
                i === 0
                  ? createDataFetchPromise(slice, indices)
                  : createDataFetchPromise(slice, indices).catch((e) => {
                      window.console.warn(
                        "[SliceTable] failed to load slice",
                        slice,
                        e
                      );
                      failures[i] = "fetch_failed";
                      return EMPTY_RESPONSE;
                    });

              const labelPromise = resolveDisplayLabel(
                slice,
                indexType
              ).catch(() => fallbackDisplayLabel(slice));

              return Promise.all([dataPromise, labelPromise]).finally(
                onSliceSettled
              );
            }
          )
        );

        if (isCancelled) return;

        const dataResponses = settled.map(([response]) => response);

        // A slice that failed after its label resolution was already under way
        // still reports raw identifiers, matching the stub column's header.
        const displayLabels = settled.map(([, label], i) =>
          failures[i] ? fallbackDisplayLabel(slicesToFetch[i]) : label
        );

        // Grab the sample/feature labels for any referenced types
        const idToLabelMappings = {} as Record<string, Record<string, string>>;

        for (const slice of slices) {
          const dataset = datasets.find(
            (d) => d.given_id === slice.dataset_id || d.id === slice.dataset_id
          );

          if (!dataset) {
            continue;
          }

          const references =
            dataset.format === "tabular_dataset"
              ? dataset.columns_metadata[slice.identifier]?.references
              : null;

          if (references && !(references in idToLabelMappings)) {
            // eslint-disable-next-line no-await-in-loop
            const identifiers = await getDimensionTypeIdentifiersPersisted(
              references
            );

            idToLabelMappings[references] = {};

            identifiers.forEach(({ id, label }) => {
              idToLabelMappings[references][id] = label;
            });
          }
        }

        // Transform the responses into table data
        const { data, columns } = transformToTableData(
          dataResponses,
          displayLabels,
          slicesToFetch,
          viewOnlySlices,
          datasets as Dataset[],
          indexType,
          idColumnDisplayName,
          labelColumnDisplayName,
          idToLabelMappings,
          getColumnDisplayOptions,
          failures,
          rowIds !== undefined
        );

        const reconciledData = reconcileRows(prevRowsRef.current, data);
        prevRowsRef.current = reconciledData;

        setState((prev) => ({
          ...prev,
          data: reconciledData,
          columns,
          entityLabel: indexType.display_name || indexType.name,
          loading: false,
          progress: null,
          error: null,
        }));
      } catch (error) {
        window.console.error(error);

        if (!isCancelled) {
          const errorMessage =
            error instanceof Error ? error.message : "Unknown error occurred";
          setState((prev) => ({
            ...prev,
            loading: false,
            progress: null,
            error: `Failed to load data: ${errorMessage}`,
          }));
        }
      }
    };

    loadData();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getColumnDisplayOptions,
    index_type_name,
    slices,
    viewOnlySlices,
    rowIds,
    // Not read by `loadData` — see `retryToken`'s comment above.
    retryToken,
  ]);

  return {
    columns: state.columns,
    data: state.data,
    loading: state.loading,
    progress: state.progress,
    error: state.error,
    entityLabel: state.entityLabel,
    exportToCsv,
  };
}
